import React, { useState, useRef } from 'react';
import { Upload, X, Sparkles, ArrowRight, MapPin, Package, Cpu, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { track, EVENTS } from '../lib/analytics';
import SEO from '../components/SEO';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

const titleCase = (s) => (s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AIImageSearch = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    setError(null);
    if (!ACCEPTED.includes(f.type)) {
      setError('Please upload a JPG, PNG, or WEBP image.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError(`Image too large (${Math.round(f.size / 1024)} KB). Max is 8 MB.`);
      return;
    }
    setFile(f);
    track(EVENTS.AI_IMAGE_UPLOADED, { mime_type: f.type, size_kb: Math.round(f.size / 1024) });
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
    setResults(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const reset = () => { setFile(null); setPreview(null); setResults(null); setError(null); };

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    setError(null);
    setResults(null);
    const startedAt = Date.now();
    track(EVENTS.IMAGE_SEARCH_STARTED, { size_kb: Math.round(file.size / 1024), mime_type: file.type });
    try {
      const fd = new FormData();
      fd.append('image', file, file.name || 'upload');
      const resp = await fetch(`${BACKEND}/api/image-search/analyze`, { method: 'POST', body: fd });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        const msg = data?.detail || `Analysis failed (HTTP ${resp.status})`;
        throw new Error(msg);
      }
      setResults(data);
      const elapsed = Date.now() - startedAt;
      track(EVENTS.IMAGE_SEARCH_COMPLETED, {
        confidence: data.confidence,
        suggested_category: data.category,
        factory_match_count: (data.supplier_recommendations || []).length,
        no_strong_match: !!data.no_strong_match,
        elapsed_ms: elapsed,
        model: data.model,
      });
      // legacy event preserved for compatibility
      track(EVENTS.AI_IMAGE_SEARCH_COMPLETED, {
        confidence: data.confidence,
        suggested_category: data.category,
        factory_match_count: (data.supplier_recommendations || []).length,
      });
    } catch (e) {
      const reason = e?.message || 'Unknown error';
      setError(reason);
      track(EVENTS.IMAGE_SEARCH_FAILED, { reason });
    } finally {
      setAnalyzing(false);
    }
  };

  const onFactoryClick = (slug, position) => {
    track(EVENTS.IMAGE_RESULT_FACTORY_CLICKED, { supplier_slug: slug, position });
  };
  const onRfqStart = (placement) => {
    track(EVENTS.IMAGE_RESULT_RFQ_STARTED, { placement });
  };

  const rec = results?.supplier_recommendations || [];

  return (
    <div data-testid="ai-image-search-page" className="bg-[#0A0A0A] text-white grain-overlay">
      <SEO
        title="AI Image Search — Find Similar Products & Factories"
        description="Upload a product photo and surface visually matched factories, estimated MOQ, sourcing regions, and likely supplier matches. AI-assisted fashion sourcing intelligence."
        keywords="AI image search, visual sourcing, factory matching, Gemini vision"
      />
      <section className="border-b border-neutral-800">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="flex items-center gap-2 label-overline text-neutral-400">
            <Cpu size={14} strokeWidth={1.5} /> / Intelligence Terminal
          </div>
          <h1 className="giwahs-blue-emphasis mt-5 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 6rem)', lineHeight: 0.92 }}>
            AI IMAGE<br />SEARCH.
          </h1>
          <p className="mt-6 max-w-xl text-neutral-300 md:text-lg">
            Upload a product photo. Get an Intelligence Brief — likely category, materials, MOQ estimate, regions, and matching factories from our verified database.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-emerald-300">
            <span className="label-overline">Live</span>
            <span className="font-mono-data text-[11px]">Powered by Gemini 3 Pro Vision · grounded on GIWAHS factory database</span>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-800">
        <div className="mx-auto max-w-[1440px] grid gap-10 px-5 py-16 md:grid-cols-2 md:px-8">
          {/* Upload terminal */}
          <div className="border border-white/20 p-6 md:p-10">
            <div className="flex items-center justify-between border-b border-white/20 pb-3">
              <span className="label-overline text-neutral-400">Upload terminal</span>
              <span className="font-mono-data text-[11px] text-neutral-500">PROTOCOL: HTTP/2 · TLS</span>
            </div>

            {!preview && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                data-testid="ai-upload-zone"
                className={`crosshair-frame mt-6 flex aspect-[5/4] cursor-pointer flex-col items-center justify-center text-center transition-colors ${dragOver ? 'text-white' : 'text-neutral-400 hover:text-white'}`}
              >
                <span className="ch-tr" /><span className="ch-bl" />
                <Upload size={28} strokeWidth={1.5} />
                <div className="mt-5 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>Drop image here</div>
                <div className="mt-2 font-mono-data text-[11px] text-neutral-500">or click to browse — JPG, PNG, WEBP · max 8MB</div>
                <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="ai-file-input" />
              </div>
            )}

            {preview && (
              <div className="mt-6">
                <div className="crosshair-frame aspect-[5/4] overflow-hidden bg-black">
                  <span className="ch-tr" /><span className="ch-bl" />
                  <img src={preview} alt="upload preview" className="h-full w-full object-cover" data-testid="ai-image-preview" />
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button onClick={analyze} disabled={analyzing} data-testid="ai-analyze-btn" className="btn-giwahs btn-giwahs-dark inline-flex items-center gap-2 disabled:opacity-50">
                    <Sparkles size={14} strokeWidth={1.5} /> {analyzing ? 'Analyzing…' : 'Find Similar Products & Factories'}
                  </button>
                  <button onClick={reset} data-testid="ai-clear-btn" className="btn-giwahs border border-white/30 bg-transparent text-white hover:bg-white hover:text-black inline-flex items-center gap-2">
                    <X size={14} strokeWidth={1.5} /> Clear
                  </button>
                </div>
                {error && (
                  <div data-testid="ai-error" className="mt-4 inline-flex items-start gap-2 border border-red-500/40 bg-red-500/10 px-3 py-2 text-red-300">
                    <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5" />
                    <span className="font-mono-data text-[12px]">{error}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Terminal log */}
          <div className="border border-white/20 p-6 md:p-10">
            <div className="flex items-center justify-between border-b border-white/20 pb-3">
              <span className="label-overline text-neutral-400">Live readout</span>
              <span className={`h-2 w-2 ${analyzing ? 'animate-pulse bg-amber-400' : error ? 'bg-red-400' : results ? 'bg-emerald-400' : 'bg-neutral-600'}`} />
            </div>
            <pre data-testid="ai-readout" className="mt-4 whitespace-pre-wrap break-words font-mono-data text-[12px] leading-relaxed text-neutral-300 min-h-[280px]">
{analyzing && `> initiating image_analysis...
  ↳ uploading to /api/image-search/analyze
  ↳ Gemini 3 Pro Vision · extracting features
  ↳ grounding against verified factory database
  ↳ resolving MOQ + region estimates...`}
{!analyzing && !results && !error && `> READY
> awaiting image upload...
> drop a product photo to begin analysis
> protocol: GIWAHS-VISION (Gemini 3 Pro)`}
{error && `> ERROR
  ↳ ${error}
> please retry with a different image`}
{results && `> ANALYSIS COMPLETE — confidence ${results.confidence}%
  ↳ category:           ${results.category}
  ↳ subcategory:        ${results.subcategory || '—'}
  ↳ materials:          ${(results.materials || []).join(', ')}
  ↳ moq_range:          ${results.moq_range}
  ↳ price_range:        ${results.price_range || '—'}
  ↳ sourcing_regions:   ${(results.sourcing_countries || []).join(', ')}
  ↳ factory_matches:    ${rec.length}${results.no_strong_match ? ' (no strong match)' : ''}
  ↳ model:              ${results.model}
> open intelligence brief below ↓`}
            </pre>
          </div>
        </div>
      </section>

      {/* Results */}
      {results && (
        <section className="bg-white text-black border-b border-black" data-testid="ai-results-section">
          <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
            <span className="label-overline">/ Intelligence Brief</span>
            <h2 className="mt-4 font-display text-4xl tracking-tighter md:text-6xl" style={{ fontWeight: 700 }}>Match results.</h2>

            <div className="mt-10 grid gap-px bg-neutral-200 sm:grid-cols-2 lg:grid-cols-4" data-testid="ai-stat-grid">
              {[
                ['Category', results.category],
                ['MOQ Estimate', results.moq_range],
                ['Wholesale Range', results.price_range || '—'],
                ['Confidence', `${results.confidence}%`],
              ].map(([l, v]) => (
                <div key={l} className="bg-white p-5">
                  <div className="label-overline text-neutral-500">{l}</div>
                  <div className="mt-3 font-display text-xl tracking-tight md:text-2xl" style={{ fontWeight: 600 }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Materials + sourcing regions */}
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <div className="border border-black p-5">
                <span className="label-overline text-neutral-500">Likely materials</span>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-materials">
                  {(results.materials || []).map((m) => (
                    <span key={m} className="giwahs-chip-ink font-mono-data text-xs">{m}</span>
                  ))}
                </div>
                {results.construction_notes && (
                  <p className="mt-4 text-sm text-neutral-700">{results.construction_notes}</p>
                )}
              </div>
              <div className="border border-black p-5">
                <span className="label-overline text-neutral-500">Recommended sourcing countries</span>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-sourcing-countries">
                  {(results.sourcing_countries || []).map((c) => (
                    <span key={c} className="giwahs-chip-ink giwahs-chip-ink--solid font-mono-data text-xs">{titleCase(c)}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* RFQ brief */}
            {results.rfq_brief && (
              <div className="mt-10 border border-black bg-neutral-50 p-6 md:p-8" data-testid="ai-rfq-brief">
                <span className="label-overline">/ Suggested RFQ brief</span>
                <p className="mt-3 max-w-3xl font-display text-lg leading-relaxed md:text-xl" style={{ fontWeight: 500 }}>{results.rfq_brief}</p>
              </div>
            )}

            {/* Risks */}
            {(results.risks || []).length > 0 && (
              <div className="mt-10 border border-amber-600/60 bg-amber-50 p-6" data-testid="ai-risks">
                <div className="flex items-center gap-2">
                  <ShieldAlert size={16} strokeWidth={1.5} />
                  <span className="label-overline">Sourcing risk notes</span>
                </div>
                <ul className="mt-3 space-y-2">
                  {results.risks.map((r, i) => (
                    <li key={i} className="text-sm text-neutral-800">— {r}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Factory matches */}
            <div className="mt-14">
              <span className="label-overline">Likely factory matches (database-grounded)</span>
              {rec.length === 0 ? (
                <div className="mt-4 border border-black bg-neutral-50 p-6" data-testid="ai-no-match">
                  <p className="font-display text-xl" style={{ fontWeight: 600 }}>No strong match in our verified database yet.</p>
                  <p className="mt-2 text-sm text-neutral-700">
                    The image is best understood as <strong>{results.category}</strong>.
                    {results.nearest_categories?.length > 0 && (
                      <> Nearest categories to explore: {results.nearest_categories.join(' · ')}.</>
                    )}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link to="/factories" className="btn-giwahs btn-giwahs-primary">Browse all factories</Link>
                    <Link to="/rfq" onClick={() => onRfqStart('no-match')} className="btn-giwahs btn-giwahs-secondary">Start an RFQ</Link>
                  </div>
                </div>
              ) : (
                <div className="mt-4 grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="ai-factory-grid">
                  {rec.map((s, i) => (
                    <Link
                      key={s.slug}
                      to={`/supplier/${s.slug}`}
                      onClick={() => onFactoryClick(s.slug, i)}
                      data-testid={`ai-factory-card-${i}`}
                      className="border border-black bg-white p-5 hover:bg-neutral-50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="label-overline">{titleCase(s.type || '')}</span>
                        <span className="font-mono-data text-xs">{s.trust_score ?? '—'}/100</span>
                      </div>
                      <h3 className="mt-3 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>{s.name}</h3>
                      <div className="mt-3 flex items-center gap-2 font-mono-data text-xs text-neutral-600">
                        <MapPin size={12} strokeWidth={1.5} /> {titleCase(s.country || '')}
                        {s.moq_min != null && (<><Package size={12} strokeWidth={1.5} className="ml-2" /> MOQ {s.moq_min}+</>)}
                      </div>
                      <p className="mt-3 text-sm text-neutral-700 line-clamp-3">{s.match_reason}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="inline-flex items-center gap-1 label-overline">View profile <ArrowRight size={12} strokeWidth={1.5} /></span>
                        <span className="font-mono-data text-[11px] text-neutral-500">match {s.confidence}%</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-12 flex flex-wrap gap-3">
              <Link
                to={`/rfq?prefill_category=${encodeURIComponent(results.category)}&prefill_brief=${encodeURIComponent(results.rfq_brief || '')}`}
                onClick={() => onRfqStart('primary-cta')}
                data-testid="ai-start-rfq-cta"
                className="btn-giwahs btn-giwahs-primary"
              >
                Start RFQ from this analysis
              </Link>
              {rec.length > 1 && (
                <Link
                  to={`/compare?suppliers=${rec.map((r) => r.slug).join(',')}`}
                  data-testid="ai-compare-cta"
                  className="btn-giwahs btn-giwahs-secondary"
                >
                  Compare suggested factories
                </Link>
              )}
              <Link to="/factories" className="btn-giwahs btn-giwahs-secondary">Browse all factories</Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default AIImageSearch;
